import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useI18n } from "@/i18n";
import { updateMenu } from "@/api/endpoints";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Pencil, AlertTriangle } from "lucide-react";

interface MenuRenameDialogProps {
  open: boolean;
  onClose: () => void;
  venueId: string;
  menuId: string;
  currentName: string;
}

export function MenuRenameDialog({ open, onClose, venueId, menuId, currentName }: MenuRenameDialogProps) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [name, setName] = useState(currentName);

  // Dialog her açıldığında mevcut adı tazele.
  useEffect(() => {
    if (open) setName(currentName);
  }, [open, currentName]);

  const mutation = useMutation({
    mutationFn: () => updateMenu(menuId, { name: name.trim() }),
    onSuccess: () => {
      // Menü slug'ı değiştiği için QR dialog'unun da yeni URL'i üretmesi adına
      // menü listesini ve venue'yu invalidate et.
      queryClient.invalidateQueries({ queryKey: ["menus", venueId] });
      queryClient.invalidateQueries({ queryKey: ["venue", venueId] });
      onClose();
    },
  });

  const trimmed = name.trim();
  const isValid = trimmed.length > 0 && trimmed !== currentName;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="size-5" />
            {t("rename_menu")}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="menu-name">{t("menu_name")}</Label>
            <Input
              id="menu-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter" && isValid && !mutation.isPending) mutation.mutate();
              }}
              className="mt-1"
            />
          </div>

          <div className="flex gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-300">
            <AlertTriangle className="mt-0.5 size-4 shrink-0" />
            <p>{t("rename_menu_warning")}</p>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>
              {t("cancel")}
            </Button>
            <Button onClick={() => mutation.mutate()} disabled={!isValid || mutation.isPending}>
              {mutation.isPending ? t("saving") : t("save")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
